import cx from 'clsx';
import React, { FunctionComponent, HTMLAttributes, useState, useEffect } from 'react';
import { getWindowDimensions, DESKTOP_BREAKPOINT, TABLET_BREAKPOINT } from 'utils/breakpoints';
import styles from './Typography.module.scss';
import typographyStyles from 'styles/global/typography.module.scss';
import utilityStyles from 'styles/global/utility.module.scss';

type IVariant =
  | 'neue_semibold_72'
  | 'neue_semibold_56'
  | 'neue_semibold_48'
  | 'neue_semibold_34'
  | 'neue_semibold_30'
  | 'neue_semibold_28'
  | 'neue_semibold_24'
  | 'neue_semibold_20'
  | 'neue_semibold_20'
  | 'neue_semibold_16'
  | 'neue_semibold_uppercase_16'
  | 'ibm_regular_24'
  | 'new_ibm_regular_16'
  | 'new_ibm_regular_16'
  | 'ibm_bold_20'
  | 'ibm_bold_16'
  // New styles
  | 'new_neue_42'
  | 'new_neue_32_h1'
  | 'new_neue_semibold_28'
  | 'new_neue_semibold_26'
  | 'new_neue_semibold_20'
  | 'new_neue_20_h2'
  | 'new_neue_16'
  | 'new_neue_12'
  | 'new_ibm_regular_16'
  | 'new_ibm_regular_20'
  | 'new_ibm_bold_20'
  | 'new_ibm_bold_16'
  | 'new_ibm_medium_20';

interface OwnProps {
  desktop?: IVariant;
  mobile: IVariant;
  tablet?: IVariant;
  align?: 'left' | 'center' | 'right';
}

type TypographyAsProps = {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'p' | 'blockquote' | 'div';
} & HTMLAttributes<HTMLOrSVGElement>;

type TypographyProps = TypographyAsProps & OwnProps;

const Typography: FunctionComponent<TypographyProps> = props => {
  const [dimensions, setDimensions] = useState(getWindowDimensions());
  const { as = 'span', mobile, tablet = mobile, desktop = tablet, align = 'left', className, ...rest } = props;

  const handleResize = () => {
    setDimensions(getWindowDimensions());
  };

  const isMobile = () => dimensions.width < TABLET_BREAKPOINT;
  const isTablet = () => dimensions.width >= TABLET_BREAKPOINT && dimensions.width < DESKTOP_BREAKPOINT;
  const isDesktop = () => dimensions.width >= DESKTOP_BREAKPOINT;

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    handleResize();
  }, []);

  const typographyClassName = cx({
    [utilityStyles.squishyText]: as !== 'p' && as !== 'blockquote',
    [styles.typo]: true,
    [styles[align]]: true,
    ...(isDesktop() && { [typographyStyles[desktop]]: true }),
    ...(isTablet() && { [typographyStyles[tablet]]: true }),
    ...(isMobile() && { [typographyStyles[mobile]]: true }),
    ...(className && { [className]: true }),
  });

  const Tag = as;

  return <Tag className={typographyClassName} {...rest} />;
};

export default Typography;
